// Admin → Import, Step 3: one panel per decision the plan needs, shown only when it has rows for
// it, each with its default already chosen (`defaultDecisions`) and a line of help saying what the
// default does. Long ones are tables with a select per row, "Set all to …" above, paged.

import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  IMPORT_BY,
  NOTES_FIELDS,
  PROSE_EMPTY,
  PROSE_TO_NOTES,
  SKIP,
  acceptLookAlikes,
  conflictGroups,
  decisionGroups,
  fieldLabel,
  overLimitCells,
  placeOf,
  placeableRows,
  proseCells,
  proseKey,
  routingStatusOptions,
  rowId,
  unknownValues,
} from "@app/shared";
import type {
  CarrierOption,
  ImportDecisions,
  ImportPlan,
  ListValueRecord,
  RowPlan,
  ValueChoice,
  WarehouseField,
} from "@app/shared";
import { Button, Input } from "@axny/platform-ui";
import { cn } from "@/lib/utils";

const PAGE = 50;
const label = (f: WarehouseField) => fieldLabel("routing-line", f);
const where = (r: Pick<RowPlan, "tab" | "n">) => `${r.tab.trim()} ${r.n}`;
const choiceKey = (c: ValueChoice | undefined) =>
  !c ? "text" : "map" in c ? `map:${c.map}` : "add" in c ? "add" : "text";
const choiceOf = (k: string): ValueChoice =>
  k.startsWith("map:") ? { map: k.slice(4) } : k === "add" ? { add: true } : { text: true };

export function DecisionsStep({
  plan,
  decisions,
  onChange,
  listValues,
  carriers,
}: {
  plan: ImportPlan;
  decisions: ImportDecisions;
  onChange: (d: ImportDecisions) => void;
  listValues: readonly ListValueRecord[];
  carriers: readonly CarrierOption[];
}) {
  const d = decisions;
  const set = <K extends keyof ImportDecisions>(k: K, v: ImportDecisions[K]) => onChange({ ...d, [k]: v });
  const rows = useMemo(() => placeableRows(plan), [plan]);
  const byId = useMemo(() => new Map(plan.rows.map((r) => [rowId(r), r])), [plan]);
  const lineOf = useMemo(() => new Map(plan.lines.map((l) => [l.key, l])), [plan]);
  const groups = useMemo(() => decisionGroups(plan, d), [plan, d.conflicts, d.ambiguous]); // eslint-disable-line react-hooks/exhaustive-deps
  const conflicts = useMemo(() => conflictGroups(plan), [plan]);
  const prose = useMemo(() => proseCells(rows), [rows]);
  const over = useMemo(() => overLimitCells(rows), [rows]);
  const unknown = useMemo(() => unknownValues(rows, listValues), [rows, listValues]);
  const statuses = useMemo(() => routingStatusOptions(listValues), [listValues]);
  const routed = rows.filter((r) => r.kind !== "pe-orders");
  const differs = rows.filter((r) => r.diffs.length);
  const exists = rows.filter((r) => r.outcome === "exists");
  const ambiguous = rows.filter((r) => r.outcome === "ambiguous");
  const columnS = rows.filter((r) => r.kind === "production" && r.typed.notes);
  const carrierTexts = [
    ...new Set(plan.entries.filter((e) => e.outcome === "import" && e.carrierText).map((e) => e.carrierText)),
  ];
  const noLine = plan.entries.filter((e) => e.outcome === "no-match");

  return (
    <div className="space-y-3" data-testid="import-decisions">
      {conflicts.length ? (
        <Panel
          title="Conflicts"
          count={conflicts.length}
          help="Rows naming the same pick tickets or SOs and landing on different lines: one decision per set of rows, for every pick ticket / SO they share. Default: the first row wins; the other row keeps its other lines."
          actions={
            <Button
              size="xs"
              variant="outline"
              onClick={() => set("conflicts", Object.fromEntries(conflicts.map((g) => [g.id, SKIP])))}
            >
              Skip them all
            </Button>
          }
        >
          {conflicts.map((g, i) => (
            <fieldset key={g.id} className="border-t py-2 text-xs">
              <legend className="font-medium">
                {g.refs.length === 1 ? g.refs[0] : `${g.refs.length} pick tickets / SOs: ${g.refs.join(", ")}`} ·{" "}
                {g.lines.map(placeOf).join(", ")}
              </legend>
              {[...g.rows, SKIP].map((id) => {
                const r = byId.get(id);
                return (
                  <label key={id} className="flex items-start gap-2 py-0.5">
                    <input
                      type="radio"
                      name={`conflict-${i}-${g.id}`}
                      checked={(d.conflicts[g.id] ?? SKIP) === id}
                      onChange={() => set("conflicts", { ...d.conflicts, [g.id]: id })}
                    />
                    {r ? (
                      <span>
                        <b>{where(r)}</b> · {r.cell} ·{" "}
                        {[r.typed.productionNotes, r.typed.routingNotes].filter(Boolean).join(" · ") || "no notes"}
                        {r.typed.readyDate ? ` · ready ${r.typed.readyDate}` : ""}
                        {r.typed.schedShip ? ` · ship ${r.typed.schedShip}` : ""}
                      </span>
                    ) : (
                      <span>Skip: none of these rows imports {g.refs.length === 1 ? g.refs[0] : "them"}</span>
                    )}
                  </label>
                );
              })}
            </fieldset>
          ))}
        </Panel>
      ) : null}

      {groups.length ? (
        <Panel
          title="Several rows on one line"
          count={groups.length}
          help="Rows of the file landing on the same line (one PO over several rows) whose entries differ. Rows with equal entries merged by themselves. Default: the first row's fields, the notes of all of them joined with “ · ”."
          actions={
            <>
              <Button
                size="xs"
                variant="outline"
                onClick={() =>
                  set(
                    "groups",
                    Object.fromEntries(
                      groups.map((g) => [g.id, { row: d.groups[g.id]?.row ?? g.rows[0]!, combine: true }]),
                    ),
                  )
                }
              >
                Set all to combine
              </Button>
              <Button
                size="xs"
                variant="outline"
                onClick={() =>
                  set(
                    "groups",
                    Object.fromEntries(
                      groups.map((g) => [g.id, { row: d.groups[g.id]?.row ?? g.rows[0]!, combine: false }]),
                    ),
                  )
                }
              >
                Set all to the ticked row wins
              </Button>
            </>
          }
        >
          <Paged items={groups}>
            {(g) => {
              const rs = g.rows.map((id) => byId.get(id)!).filter(Boolean);
              const fields = [...new Set(rs.flatMap((r) => Object.keys(r.typed)))] as WarehouseField[];
              const choice = d.groups[g.id] ?? { row: g.rows[0]!, combine: true };
              const put = (c: { row: string; combine: boolean }) => set("groups", { ...d.groups, [g.id]: c });
              return (
                <div key={g.id} className="overflow-x-auto border-t py-2 text-xs">
                  <div className="mb-1 flex items-center gap-3">
                    <b>{placeOf(g.line)}</b>
                    <span className="text-muted-foreground">
                      {g.fields === "pe" ? "PE Order columns" : "line fields"}
                    </span>
                    <label className="ml-auto flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={choice.combine}
                        onChange={(e) => put({ ...choice, combine: e.target.checked })}
                      />
                      Combine: the notes joined, the other fields from the ticked row
                    </label>
                  </div>
                  <table className="text-[11px]">
                    <thead>
                      <tr>
                        <th />
                        {rs.map((r) => (
                          <th key={rowId(r)} className="px-2 text-left font-medium whitespace-nowrap">
                            <label className="flex items-center gap-1">
                              <input
                                type="radio"
                                name={`group-${g.id}`}
                                checked={choice.row === rowId(r)}
                                onChange={() => put({ ...choice, row: rowId(r) })}
                              />
                              {where(r)} wins
                            </label>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {fields.map((f) => (
                        <tr key={f} className="border-t align-top">
                          <td className="text-muted-foreground pr-2 whitespace-nowrap">
                            {label(f)}
                            {NOTES_FIELDS.includes(f) ? " (notes)" : ""}
                          </td>
                          {rs.map((r) => (
                            <td key={rowId(r)} className="max-w-60 px-2 break-words">
                              {r.typed[f] ?? ""}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            }}
          </Paged>
        </Panel>
      ) : null}

      {ambiguous.length ? (
        <Panel
          title="POs on several Deposco lines"
          count={ambiguous.length}
          help="The row names a PO and no DC, and that PO is on several open Deposco lines. Default: skip the row."
        >
          {ambiguous.map((r) => (
            <fieldset key={rowId(r)} className="border-t py-2 text-xs">
              <legend className="font-medium">
                {where(r)} · {r.customer} · {r.cell}
              </legend>
              {[...r.candidates, SKIP].map((k) => {
                const l = lineOf.get(k);
                const [, , po, dc] = k.split("|");
                return (
                  <label key={k} className="flex items-center gap-2 py-0.5">
                    <input
                      type="radio"
                      name={`amb-${rowId(r)}`}
                      checked={(d.ambiguous[rowId(r)] ?? SKIP) === k}
                      onChange={() => set("ambiguous", { ...d.ambiguous, [rowId(r)]: k })}
                    />
                    {k === SKIP ? (
                      "Skip the row"
                    ) : (
                      <span>
                        {l?.customer || "—"} · PO {po} · {dc ? `DC ${dc}` : "no DC"} · {l?.refs.length ?? 0} SOs
                        {l?.status ? ` · ${l.status}` : ""}
                      </span>
                    )}
                  </label>
                );
              })}
            </fieldset>
          ))}
        </Panel>
      ) : null}

      {exists.length ? (
        <Panel
          title="Lines already saved"
          count={exists.length}
          help="Their line already has a record (an earlier run, or use). Default: skip them — keep what the app has. Overwrite writes the file's typed fields only: never the baseline, the pick tickets, the review or who grouped it."
        >
          <Choice
            name="exists"
            value={d.exists}
            onChange={(v) => set("exists", v as ImportDecisions["exists"])}
            options={[
              ["skip", "Skip (keep what the app has)"],
              ["overwrite", "Overwrite the typed fields with the file's"],
            ]}
          />
        </Panel>
      ) : null}

      {differs.length ? (
        <Panel
          title="Differences from A2000 / Deposco"
          count={differs.length}
          help="QTY, FIRST SHIP or CANCEL in the row differ from the source. Default: approve with the app's values (the row is listed in the result file). UPDATED: the file's values in the baseline — QTY, the row's total, on the line's first pick ticket — so the Worklist shows old → new. New lines only."
          actions={
            <SetAll
              options={[
                ["approve", "Approve with the app's values"],
                ["updated", "Import as UPDATED"],
              ]}
              onSet={(v) =>
                set("differences", Object.fromEntries(differs.map((r) => [rowId(r), v as "approve" | "updated"])))
              }
            />
          }
        >
          <Paged items={differs}>
            {(r) => (
              <Row key={rowId(r)} r={r}>
                <span className="text-muted-foreground">
                  {r.diffs.map((x) => `${x.field} ${x.file} → ${x.app}`).join(" · ")}
                </span>
                <select
                  className="ml-auto rounded border px-1"
                  value={d.differences[rowId(r)] ?? "approve"}
                  onChange={(e) =>
                    set("differences", { ...d.differences, [rowId(r)]: e.target.value as "approve" | "updated" })
                  }
                >
                  <option value="approve">Approve with the app's values</option>
                  <option value="updated">Import as UPDATED</option>
                </select>
              </Row>
            )}
          </Paged>
        </Panel>
      ) : null}

      {prose.length ? (
        <Panel
          title="READY DATE / SCHEDULED SHIP DATE prose"
          count={prose.length}
          help="A date column holding words. Default: the first date the text names; none, the field stays empty and the text goes to Notes."
        >
          <Paged items={prose}>
            {(p) => {
              const k = proseKey(p.row, p.field);
              return (
                <Row key={k} r={p.row}>
                  <span>
                    {label(p.field)}: <i>{p.text}</i>
                  </span>
                  <select
                    className="ml-auto rounded border px-1"
                    value={d.prose[k] ?? PROSE_TO_NOTES}
                    onChange={(e) => set("prose", { ...d.prose, [k]: e.target.value })}
                  >
                    {p.dates.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                    <option value={PROSE_TO_NOTES}>Leave empty, the text to Notes</option>
                    <option value={PROSE_EMPTY}>Leave empty</option>
                  </select>
                </Row>
              );
            }}
          </Paged>
        </Panel>
      ) : null}

      {routed.length ? (
        <Panel
          title="Routing status"
          count={routed.length}
          help="Preselected by the rule: ROUTED and a date → Routed on that date (the row's year when the text has none); PRE ROUTED → Pre-routed; else a Routing Reference → Routed (the app sets Routed on to today); else Not routed. The texts the guess came from are shown."
          actions={
            <SetAll
              options={statuses.map((s) => [s, s])}
              onSet={(v) =>
                set(
                  "routing",
                  Object.fromEntries(routed.map((r) => [rowId(r), { status: v, on: d.routing[rowId(r)]?.on ?? "" }])),
                )
              }
            />
          }
        >
          <Paged items={routed}>
            {(r) => {
              const v = d.routing[rowId(r)] ?? { status: "Not routed", on: "" };
              const put = (x: Partial<typeof v>) => set("routing", { ...d.routing, [rowId(r)]: { ...v, ...x } });
              return (
                <Row key={rowId(r)} r={r}>
                  <span className="text-muted-foreground">{r.routingFrom || "no text"}</span>
                  <select
                    className="ml-auto rounded border px-1"
                    value={v.status}
                    onChange={(e) => put({ status: e.target.value })}
                  >
                    {[...new Set([v.status, ...statuses])].map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <Input
                    type="date"
                    className="h-6 w-36 text-xs"
                    value={v.on}
                    disabled={v.status === "Not routed"}
                    onChange={(e) => put({ on: e.target.value })}
                    aria-label="Routed on"
                  />
                </Row>
              );
            }}
          </Paged>
        </Panel>
      ) : null}

      {unknown.length ? (
        <Panel
          title="List values the app does not know"
          count={unknown.length}
          help="Work Needed and Assigned to split on “/”; a Lead's trailing date is cut (said in the result); an Assigned to's (45MINS) is the Estimate. Each value: a value of the list (the look-alike preselected), added to the list, or text — Work Needed's into Production Notes, the others into Notes (PE ORDERS: Comments)."
          actions={
            <Button size="xs" variant="outline" onClick={() => onChange(acceptLookAlikes(plan, d))}>
              Accept every look-alike guess
            </Button>
          }
        >
          <Paged items={unknown}>
            {(u) => {
              const k = `${u.list}|${u.value.toUpperCase()}`;
              const options = listValues.filter((v) => v.list === u.list && v.retired !== "Yes").map((v) => v.value);
              return (
                <div key={k} className="flex flex-wrap items-center gap-2 border-t py-1 text-xs">
                  <span className="text-muted-foreground w-36">{u.list}</span>
                  <b>{u.value}</b>
                  <span className="text-muted-foreground">{u.rows.length} rows</span>
                  <select
                    className="ml-auto max-w-72 rounded border px-1"
                    value={choiceKey(d.values[k])}
                    onChange={(e) => set("values", { ...d.values, [k]: choiceOf(e.target.value) })}
                  >
                    {options.map((o) => (
                      <option key={o} value={`map:${o}`}>
                        {o}
                      </option>
                    ))}
                    <option value="add">Add it to the list</option>
                    <option value="text">Treat as text</option>
                  </select>
                </div>
              );
            }}
          </Paged>
        </Panel>
      ) : null}

      {over.length ? (
        <Panel
          title="Text over a field's limit"
          count={over.length}
          help="Default: cut at the limit, the rest moved to Notes. Or edit it here."
        >
          {over.map((o) => {
            const k = `${rowId(o.row)}|${o.field}`;
            const c = d.overLimit[k] ?? { cut: true };
            return (
              <div key={k} className="space-y-1 border-t py-2 text-xs">
                <div>
                  <b>{where(o.row)}</b> · {label(o.field)} · {o.text.length} characters, at most {o.max}
                </div>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={"cut" in c}
                    onChange={() => set("overLimit", { ...d.overLimit, [k]: { cut: true } })}
                  />
                  Cut at {o.max}, the rest to Notes
                </label>
                <label className="flex items-start gap-2">
                  <input
                    type="radio"
                    checked={"edit" in c}
                    onChange={() => set("overLimit", { ...d.overLimit, [k]: { edit: o.text.slice(0, o.max) } })}
                  />
                  <textarea
                    className="w-full rounded border p-1"
                    rows={2}
                    maxLength={o.max}
                    value={"edit" in c ? c.edit : o.text.slice(0, o.max)}
                    onChange={(e) => set("overLimit", { ...d.overLimit, [k]: { edit: e.target.value } })}
                  />
                </label>
              </div>
            );
          })}
        </Panel>
      ) : null}

      {columnS.length ? (
        <Panel
          title="Column S notes"
          count={columnS.length}
          help="The headerless column after INV. on the check-off. Default: appended to Notes (the app's “INV.” column). The INV. tick box itself is not imported."
        >
          <Choice
            name="column-s"
            value={d.columnS}
            onChange={(v) => set("columnS", v as ImportDecisions["columnS"])}
            options={[
              ["append", "Append to Notes"],
              ["ignore", "Ignore"],
            ]}
          />
        </Panel>
      ) : null}

      {carrierTexts.length ? (
        <Panel
          title="Shipping Schedule carriers"
          count={carrierTexts.length}
          help="Each carrier text of the schedule: a carrier of A2000's and Deposco's list (the look-alike on name or SCAC preselected), or none — its text goes to the pickup's Notes. Never a typed carrier."
        >
          {carrierTexts.map((t) => (
            <div key={t} className="flex items-center gap-2 border-t py-1 text-xs">
              <b className="w-72 truncate">{t}</b>
              <select
                className="ml-auto max-w-80 rounded border px-1"
                value={d.carriers[t] ?? "none"}
                onChange={(e) => set("carriers", { ...d.carriers, [t]: e.target.value })}
              >
                <option value="none">None (the text to the pickup's Notes)</option>
                {carriers.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.name} ({c.code})
                  </option>
                ))}
              </select>
            </div>
          ))}
        </Panel>
      ) : null}

      {noLine.length ? (
        <Panel
          title="Shipping Schedule entries that match no line"
          count={noLine.length}
          help="Skipped: no open pick ticket, SO or PO of theirs is on a line. Listed in the result file."
        >
          {noLine.map((e) => (
            <div key={`${e.tab}|${e.n}`} className="border-t py-1 text-xs">
              {e.tab.trim()} {e.n} · {e.day} · {e.customer} · {[...e.pos, ...e.refs].join(", ")}
            </div>
          ))}
        </Panel>
      ) : null}

      <details className="rounded-md border p-3 text-xs">
        <summary className="cursor-pointer font-medium">How the fields map</summary>
        <FieldMap />
      </details>
    </div>
  );
}

function Panel({
  title,
  count,
  help,
  actions,
  children,
}: {
  title: string;
  count: number;
  help: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <details
      className="rounded-md border p-3"
      data-testid={`import-panel-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      <summary className="cursor-pointer">
        <span className="text-sm font-medium">{title}</span>
        <span className="text-muted-foreground ml-2 text-sm tabular-nums">{count.toLocaleString("en-US")}</span>
        <p className="text-muted-foreground text-xs">{help}</p>
      </summary>
      {actions ? <div className="my-2 flex flex-wrap gap-2">{actions}</div> : null}
      <div className="mt-2">{children}</div>
    </details>
  );
}

function Row({ r, children }: { r: RowPlan; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-t py-1 text-xs">
      <b className="w-44 shrink-0">{where(r)}</b>
      <span className="w-40 shrink-0 truncate" title={r.cell}>
        {r.customer}
      </span>
      {children}
    </div>
  );
}

function Choice({
  name,
  value,
  onChange,
  options,
}: {
  name: string;
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
}) {
  return (
    <div className="flex flex-wrap gap-4 text-sm">
      {options.map(([v, text]) => (
        <label key={v} className="flex items-center gap-2">
          <input type="radio" name={name} checked={value === v} onChange={() => onChange(v)} />
          {text}
        </label>
      ))}
    </div>
  );
}

function SetAll({ options, onSet }: { options: [string, string][]; onSet: (v: string) => void }) {
  return (
    <label className="flex items-center gap-2 text-xs">
      Set all to
      <select className="rounded border px-1" value="" onChange={(e) => e.target.value && onSet(e.target.value)}>
        <option value="">…</option>
        {options.map(([v, text]) => (
          <option key={v} value={v}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );
}

/** A long list, `PAGE` at a time. */
function Paged<T>({ items, children }: { items: readonly T[]; children: (item: T) => ReactNode }) {
  const [page, setPage] = useState(0);
  const pages = Math.max(1, Math.ceil(items.length / PAGE));
  return (
    <>
      {items.slice(page * PAGE, page * PAGE + PAGE).map(children)}
      {pages > 1 ? (
        <div className="flex items-center gap-2 pt-2 text-xs">
          <Button size="xs" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <span className={cn("text-muted-foreground tabular-nums")}>
            {page * PAGE + 1}–{Math.min(items.length, page * PAGE + PAGE)} of {items.length.toLocaleString("en-US")}
          </span>
          <Button size="xs" variant="outline" disabled={page >= pages - 1} onClick={() => setPage(page + 1)}>
            Next
          </Button>
        </div>
      ) : null}
    </>
  );
}

const MAP: [string, string][] = [
  [
    "PE check-off",
    "READY DATE (a day, or ASAP), Work Needed (list values, “; ” between them), Production Notes, Routing status + Routed on, Routing Notes (the text minus the ROUTED part), Routing Reference, SCHEDULED SHIP DATE, Inventory Notes, Lead, TIMMING FOR REWORK, Notes (column S)",
  ],
  [
    "HT & SM LOG",
    "Routing status / Routed on from Routing Status, Routing Reference from Routing Conf#, SCHEDULED SHIP DATE, Work Needed, Notes; Carton Labels Sent TRUE → “Carton labels sent” in Production Notes",
  ],
  [
    "PE ORDERS",
    "PE Order Ship Date (or ASAP), Pull Group #, Loc Qty, Assigned To + Estimate, Status, Start / End Time (a day and h:mm AM/PM), Pallets, Comments",
  ],
  [
    "Shipping Schedule",
    "One pickup per entry (PE, the day section's date, App Time, the carrier picked, Door, PICK UP # / SHIPMENT # / BOL #, Notes, Scheduled) and one shipping row per line (CTNS, Loader, In, Out, Trailer#, WMS / ASN / TRIP); PLTS goes to the line's Pallets; the line's SCHEDULED SHIP DATE is the pickup's day",
  ],
  [
    "Every new line",
    `Approved, reviewed and grouped by “${IMPORT_BY}”: its pick tickets as approved from A2000 / Deposco; customer, dates, QTY, PO / PT and WMS never from the file`,
  ],
];

function FieldMap() {
  return (
    <table className="mt-2 w-full">
      <tbody>
        {MAP.map(([tab, text]) => (
          <tr key={tab} className="border-t align-top">
            <td className="w-40 py-1 pr-2 font-medium">{tab}</td>
            <td className="py-1">{text}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
